import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Utils } from 'utils';
import Store from 'store';
import { useAuth } from '../../contexts/Auth.contexts';
import Axios from 'axios';

const useChatbotProfile = () => {
    // Estados principais do chatbot

    const [initChat, setInitChat] = useState(false);
    const [tokenChat, setTokenChat] = useState('');
    const [idSession, setIdSession] = useState(0);
    const [chatProfiles, setChatProfiles] = useState([]);
    const [empresasList, setEmpresas] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [transitionIn, setTransitionIn] = useState(true);
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [searchCliente, setSearchCliente] = useState('');
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [loadingPage, setLoadingPage] = useState(false);

    const [chatProfile, setChatProfile] = useState([]);

    const { profile } = useAuth();

    const { t: translate } = useTranslation();
    const currentLang = window.localStorage.i18nextLng;

    const goLink = (link) => history.push(Utils.setPrefix(link));

    document.title = translate('home.head.title');

    // URL base da API do chatbot
    const apiCHAT = useMemo(() => {
        return Axios.create({
            baseURL:
                process.env.NODE_ENV !== 'development'
                    ? process.env.REACT_APP_CHATBOT_API_URL_PRODUCTION
                    : process.env.REACT_APP_CHATBOT_API_URL_DEVELOPMENT,
        });
    }, []);

    // Proteção para rodar init só uma vez
    const initHasRunRef = useRef(false);
    useEffect(() => {
        setIdSession(null);
        if (initHasRunRef.current) return;
        initHasRunRef.current = true;

        window.scrollTo(0, 0);

        const init = async () => {
            let token = tokenChat;

            if (!token) {
                token = await gerarTokenChat();
                if (!token) return;
            }
        };

        obterProfile();

        init();
    }, [clienteSelecionado, search]);

    const fetchClienteSearch = useCallback(
        async (searchCliente) => {
            let token = tokenChat;

            if (!token) {
                token = await gerarTokenChat();
                if (!token) return;
            }

            setSearchCliente(searchCliente);

            if (!searchCliente || !searchCliente.trim() || searchCliente.length === 0) {
                setEmpresas([]);
                return;
            }
            // Busca usando o valor mais atualizado
            await fetchEmpresas(null, token, searchCliente);
        },
        [searchCliente, empresasList],
    );

    const urlChat = (path) => {
        return `${apiCHAT.defaults.baseURL}${path}`;
    };

    const gerarTokenChat = useCallback(async () => {
        const url = urlChat('token/api');
        const dataLogin = Store.get('logins') ? JSON.parse(Store.get('logins')) : {};

        const tokenAltum = Store.get('token_altum');
        const userName = dataLogin.login || String(profile.pess_usuario) || 'unknown_user';

        try {
            const response = await apiCHAT.post(
                url,
                {
                    username: userName,
                    token: tokenAltum,
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                },
            );
            const chatToken = response.data.access_token;
            const idUsuario = response.data.id_usuario;

            Store.set('id_usuario', idUsuario);
            Store.set('token_chat', chatToken);
            setTokenChat(chatToken);

            return chatToken;
        } catch (error) {
            console.error('Erro ao gerar token:', error);
            return null;
        }
    }, []);

    const fetchChatProfile = useCallback(
        async (page = 1, chatToken = tokenChat, buscar) => {
            let token = chatToken;

            if (!token) {
                token = await gerarTokenChat();
                setTokenChat(token);
                if (!token) return;
            }

            try {
                const params = {
                    page,
                    ...{
                        buscar: buscar,
                        pess_id_cliente_principal: clienteSelecionado,
                    },
                };

                const response = await apiCHAT.get('user/list/paginate', {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                    body: {},
                });

                setChatProfiles(response.data.items);
                setCurrentPage(response.data.page);
                setTotalPages(response.data.total_pages);
            } catch (error) {
                console.error('Erro ao buscar sessões:', error);
            }
        },

        [clienteSelecionado, tokenChat, chatProfiles],
    );

    const fetchProfileSearch = useCallback(
        async (search) => {
            let token = tokenChat;

            if (!token) {
                token = await gerarTokenChat();
                if (!token) return;
            }

            search = search === '' ? null : search;
            setSearch(search); // atualiza o estado visível no input
            // Busca usando o valor mais atualizado
            if (clienteSelecionado) {
                await fetchChatProfile(1, token, search);
            }
        },
        [search, clienteSelecionado, fetchChatProfile],
    );

    const fetchEmpresas = useCallback(
        async (page = 1, chatToken = tokenChat, buscar) => {
            if (!chatToken) return;

            if (!buscar || !buscar.trim()) {
                setEmpresas([]);
                return;
            }

            try {
                const params = {
                    ...(buscar ? { buscar: buscar } : {}),
                };

                const response = await apiCHAT.get('/cliente/list', {
                    params,
                    headers: { Authorization: `Bearer ${chatToken}` },
                    body: {},
                });

                setEmpresas(response.data);
            } catch (error) {
                console.error('Erro ao buscar Empresas:', error);
            }
            setSearch(buscar);
        },
        [tokenChat, searchCliente],
    );

    const obterProfile = useCallback(async () => {
        let token = tokenChat;

        if (!token) {
            token = await gerarTokenChat();

            setTokenChat(token);

            if (!token) return;
        }

        try {
            const params = {
                id_usuario: profile.pess_id_pessoa,
            };

            const response = await apiCHAT.get('/chat_perfil/show', {
                params,
                headers: { Authorization: `Bearer ${token}` },
                body: {},
            });

            setChatProfile(response.data);
        } catch (error) {
            console.error('Erro ao buscar perfil:', error);
        }
    }, []);

    const handleStartWithSession = useCallback((sessionId) => {
        Store.set('id_session_chat', sessionId);
        setIdSession(sessionId);
        setInitChat(true);
    }, []);

    const criarPerfilChat = useCallback(
        async (userId, clientId, nivel, ausencia) => {
            let token = tokenChat;

            if (!token) {
                token = await gerarTokenChat();
                setTokenChat(token);
                if (!token) return false;
            }

            try {
                const response = await apiCHAT.post(
                    '/chat_perfil/create',
                    {
                        id_usuario: userId,
                        nivel_acesso: nivel,
                        ausencia: ausencia,
                        id_cliente: clientId,
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    },
                );
                console.log('Perfil criado com sucesso');
                return true;
            } catch (error) {
                console.error('Erro ao criar perfil', error);
                return false;
            }
        },
        [tokenChat],
    );

    const removerPerfilChat = useCallback(
        async (userId, clientId) => {
            let token = tokenChat;

            if (!token) {
                token = await gerarTokenChat();
                setTokenChat(token);
                if (!token) return false;
            }

            try {
                await apiCHAT.delete('/chat_perfil/delete', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        user_id: userId,
                        client_id: clientId,
                    },
                });
                console.log('Perfil excluído com sucesso');
                return true;
            } catch (error) {
                console.error('Erro ao excluir perfil', error);
                return false;
            }
        },
        [tokenChat],
    );

    // Intercepta 401 para resetar sessão
    apiCHAT.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response && error.response.status === 401) {
                Store.remove('token_chat');
                Store.remove('id_session_chat');
                setInitChat(false);
                setTokenChat(null);
                setIdSession(null);
            }
            return Promise.reject(error);
        },
    );

    return {
        currentLang,
        idSession,
        translate,
        Utils,
        initChat,
        tokenChat,
        chatProfiles,
        currentPage,
        totalPages,
        fetchChatProfile,
        transitionIn,
        handleStartWithSession,
        goLink,
        fetchProfileSearch,
        loadingPage,
        clienteSelecionado,
        fetchClienteSearch,
        empresasList,
        fetchEmpresas,
        setClienteSelecionado,
        setChatProfiles,
        criarPerfilChat,
        removerPerfilChat,
        chatProfile,
        obterProfile,
        profile,
    };
};

export default useChatbotProfile;
